import { Boundary, Point, CameraOptions, Scalable, Rotatable } from '../interface';
import Vibrator from './Vibrator';
class Camera {
  public readonly viewport: [Point, Point, Point, Point];
  public readonly viewportScale: number;
  public readonly vibrator: Vibrator;
  public readonly epsilon: number;

  public view?: Point & Scalable & Rotatable;
  public width: number;
  public height: number;
  public boundary: Boundary<Point>;
  public disabled: boolean;

  public zoom: number;
  public targetZoom: number;
  private prevZoom: number;
  public zoomChasingCoefficient: number;

  public position: Point;
  public targetPosition: Point;
  private prevPosition: Point;
  public positionChasingCoefficient: number;

  public rotation: number;
  public targetRotation: number;
  private prevRotation: number;
  public rotationChasingCoefficient: number;

  public offset: Point;

  constructor(options: Partial<CameraOptions>) {
    this.view = options.view;
    this.width = options?.width || 0;
    this.height = options?.height || 0;
    this.boundary = options?.boundary || { min: { x: -Infinity, y: -Infinity }, max: { x: Infinity, y: Infinity } };
    this.disabled = options?.disabled || false;

    this.viewportScale = options?.viewportScale || 1;
    this.vibrator = new Vibrator(this.view);
    this.epsilon = 0.001;
    this.viewport = [
      { x: 0, y: 0 },
      { x: this.width, y: 0 },
      { x: this.width, y: this.height },
      { x: 0, y: this.height },
    ];

    this.zoom = 1;
    this.targetZoom = 1;
    this.prevZoom = 1;
    this.zoomChasingCoefficient = options?.zoomChasingCoefficient || 0.01;

    this.rotation = 0;
    this.targetRotation = 0;
    this.prevRotation = 0;
    this.rotationChasingCoefficient = options?.rotationChasingCoefficient || 0.03;

    this.position = { x: 0, y: 0 };
    this.targetPosition = { x: 0, y: 0 };
    this.prevPosition = { x: 0, y: 0 };
    this.positionChasingCoefficient = options?.positionChasingCoefficient || 0.03;

    this.offset = { x: 0, y: 0 };
  }

  public update(elapsed: number): void {
    if (this.disabled) return;

    this.chase(elapsed);
    this.confine();
    this.apply();
    this.calculateViewport();

    this.vibrator.update(elapsed);
  }

  public enable(): void {
    this.disabled = false;
  }

  public disable(): void {
    this.disabled = true;
  }

  public setBoundary(boundary: Boundary<Point>): void {
    this.boundary = boundary;
  }

  public setView(view: Point & Scalable & Rotatable): void {
    this.view = view;
    this.vibrator.setTarget(this.view);
  }

  public resize(width: number = this.width, height: number = this.height): void {
    const targetPosition = this.targetPosition;

    this.width = width;
    this.height = height;

    this.setTargetPosition(
      {
        x: this.position.x - this.offset.x,
        y: this.position.y - this.offset.y,
      },
      true
    );
    this.setTargetPosition(targetPosition);
    this.calculateViewport(true);
  }

  public setTargetPosition(position: Point, direct: boolean = false): void {
    if (direct) {
      this.position.x = position.x + this.offset.x;
      this.position.y = position.y + this.offset.y;
      this.confine();
      this.apply();
      this.calculateViewport();
    }
    this.targetPosition = position;
  }

  public setTargetZoom(zoom: number, direct: boolean = false): void {
    if (direct) {
      this.zoom = zoom;
      this.confine();
      this.apply();
      this.calculateViewport();
    }
    this.targetZoom = zoom;
  }

  public setTargetRotation(rotation: number, direct: boolean = false): void {
    if (direct) {
      this.rotation = rotation;
      this.confine();
      this.apply();
      this.calculateViewport();
    }
    this.targetRotation = rotation;
  }

  public setOffset(offset: Point): void {
    this.offset = offset;
  }

  public globalPointToViewportPoint(point: Point): Point {
    return {
      x:
        this.viewport[0].x +
        (point.x * Math.cos(this.rotation)) / this.zoom +
        (point.y * Math.sin(this.rotation)) / this.zoom,
      y:
        this.viewport[0].y +
        (point.y * Math.cos(-this.rotation)) / this.zoom +
        (point.x * Math.sin(-this.rotation)) / this.zoom,
    };
  }

  public viewportPointToGlobalPoint(point: Point): Point {
    return {
      x:
        ((point.x - this.viewport[0].x) * Math.cos(-this.rotation) +
          (point.y - this.viewport[0].y) * Math.sin(-this.rotation)) *
        this.zoom,
      y:
        ((point.y - this.viewport[0].y) * Math.cos(this.rotation) +
          (point.x - this.viewport[0].x) * Math.sin(this.rotation)) *
        this.zoom,
    };
  }

  private chase(elapsed: number): void {
    this.chasePosition(elapsed);
    this.chaseZoom(elapsed);
    this.chaseRotation(elapsed);
  }

  private chasePosition(elapsed: number): void {
    if (
      this.targetPosition.x + this.offset.x === this.position.x &&
      this.targetPosition.y + this.offset.y === this.position.y
    ) {
      return;
    }

    if (this.positionChasingCoefficient * elapsed < 1) {
      this.position.x +=
        (this.targetPosition.x + this.offset.x - this.position.x) * this.positionChasingCoefficient * elapsed;
      this.position.y +=
        (this.targetPosition.y + this.offset.y - this.position.y) * this.positionChasingCoefficient * elapsed;
      if (
        this.position.x !== this.targetPosition.x + this.offset.x &&
        Math.abs(this.position.x - this.targetPosition.x + this.offset.x) <= this.epsilon
      ) {
        this.position.x = this.targetPosition.x + this.offset.x;
      }
      if (
        this.position.y !== this.targetPosition.y + this.offset.y &&
        Math.abs(this.position.y - this.targetPosition.y + this.offset.y) <= this.epsilon
      ) {
        this.position.y = this.targetPosition.y + this.offset.y;
      }
    } else {
      this.position.x = this.targetPosition.x + this.offset.x;
      this.position.y = this.targetPosition.y + this.offset.y;
    }
  }

  private chaseZoom(elapsed: number): void {
    if (this.targetZoom === this.zoom) return;

    if (this.zoomChasingCoefficient * elapsed < 1) {
      this.zoom += (this.targetZoom - this.zoom) * this.zoomChasingCoefficient * this.zoom * elapsed;

      if (Math.abs(this.targetZoom - this.zoom) <= this.epsilon) {
        this.zoom = this.targetZoom;
      }
    } else {
      this.zoom = this.targetZoom;
    }
  }

  private chaseRotation(elapsed: number): void {
    if (this.targetRotation === this.rotation) return;

    if (this.rotationChasingCoefficient * elapsed < 1) {
      this.rotation += (this.targetRotation - this.rotation) * this.rotationChasingCoefficient * elapsed;

      if (Math.abs(this.targetRotation - this.rotation) <= this.epsilon) {
        this.rotation = this.targetRotation;
      }
    } else {
      this.rotation = this.targetRotation;
    }
  }

  private apply(): void {
    if (!this.view) return;

    this.view.scale.x = this.view.scale.y = this.zoom;
    this.view.rotation = this.rotation;
    this.view.x =
      Math.cos(this.rotation) * -(this.position.x * this.zoom) -
      Math.sin(this.rotation) * -(this.position.y * this.zoom) +
      this.width / 2;
    this.view.y =
      Math.sin(this.rotation) * -(this.position.x * this.zoom) +
      Math.cos(this.rotation) * -(this.position.y * this.zoom) +
      this.height / 2;
  }

  private confine(): void {
    if (!this.boundary) return;

    if (this.width >= (this.boundary.max.x - this.boundary.min.x) * this.zoom) {
      this.position.x = (this.boundary.max.x + this.boundary.min.x) / 2;
    } else if (this.position.x * this.zoom - this.width / 2 <= this.boundary.min.x * this.zoom) {
      this.position.x = (this.boundary.min.x * this.zoom + this.width / 2) / this.zoom;
    } else if (this.position.x * this.zoom + this.width / 2 >= this.boundary.max.x * this.zoom) {
      this.position.x = (this.boundary.max.x * this.zoom - this.width / 2) / this.zoom;
    }

    if (this.height >= (this.boundary.max.y - this.boundary.min.y) * this.zoom) {
      this.position.y = (this.boundary.max.y + this.boundary.min.y) / 2;
    } else if (this.position.y * this.zoom - this.height / 2 <= this.boundary.min.y * this.zoom) {
      this.position.y = (this.boundary.min.y * this.zoom + this.height / 2) / this.zoom;
    } else if (this.position.y * this.zoom + this.height / 2 >= this.boundary.max.y * this.zoom) {
      this.position.y = (this.boundary.max.y * this.zoom - this.height / 2) / this.zoom;
    }
  }

  private get isDirty(): boolean {
    return (
      this.position.x !== this.prevPosition.x ||
      this.position.y !== this.prevPosition.y ||
      this.zoom !== this.prevZoom ||
      this.rotation !== this.prevRotation
    );
  }

  private calculateViewport(force: boolean = false): void {
    if (this.isDirty || force) {
      const r = Math.sqrt((this.width / 2 / this.zoom) ** 2 + (this.height / 2 / this.zoom) ** 2) * this.viewportScale;
      const radianA = Math.atan2(-this.height, -this.width) - this.rotation;
      const radianB = Math.atan2(-this.height, this.width) - this.rotation;

      this.viewport[0].x = this.position.x + Math.cos(radianA) * r;
      this.viewport[0].y = this.position.y + Math.sin(radianA) * r;
      this.viewport[1].x = this.position.x + Math.cos(radianB) * r;
      this.viewport[1].y = this.position.y + Math.sin(radianB) * r;
      this.viewport[2].x = this.position.x - Math.cos(radianA) * r;
      this.viewport[2].y = this.position.y - Math.sin(radianA) * r;
      this.viewport[3].x = this.position.x - Math.cos(radianB) * r;
      this.viewport[3].y = this.position.y - Math.sin(radianB) * r;

      this.prevPosition.x = this.position.x;
      this.prevPosition.y = this.position.y;
      this.prevZoom = this.zoom;
      this.prevRotation = this.rotation;
    }
  }
}

export default Camera;
